import React from 'react';
import {
  Grid,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  computeAssemblySelfComparison,
  computeAssemblyComparison,
  findAirframeAssembly,
  parseValueByUnit,
  formatValueByUnit
} from '../components/tsrComparisonUtils';
import { generateAssemblyCode } from '../components/componentFormUtils';
import { getLiveRawForTsr } from '../../utils/liveTlbUsageMapping';

const getUnitForRecord = (record) =>
  record?.elapsedTime?.unit ||
  record?.limit?.unit ||
  record?.remainingTime?.unit ||
  '';

const getDisplayValue = (value) => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
};

const AssemblyLiveComparisonSection = ({
  timeSinceRecords,
  assemblies,
  selectedAssembly,
  currentUsageTotals,
  effectiveTlbUsageMapping = null
}) => {
  const records = Array.isArray(timeSinceRecords) ? timeSinceRecords : [];
  const { assembly: airframe, error: airframeError } = findAirframeAssembly(assemblies || []);
  const airframeRecords = airframe?.timeSinceRecords || [];
  const currentValues = currentUsageTotals?.values || null;
  const liveValuesAvailable = currentValues && typeof currentValues === 'object';

  const isAirframeAssembly =
    selectedAssembly?.typeName === 'Airframe' ||
    selectedAssembly?.type?.name === 'Airframe';

  const hasRecords = records.length > 0;

  const toNonEmpty = (v) => v != null && String(v).trim() !== '';

  const airframeTotalRaw = (unit, label) => {
    const { raw } = getLiveRawForTsr(currentValues, effectiveTlbUsageMapping, {
      unit,
      label,
      assemblyRole: 'Airframe'
    });
    return raw;
  };

  const engineHoursRaw = (label) => {
    const { raw } = getLiveRawForTsr(currentValues, effectiveTlbUsageMapping, {
      unit: 'Hours',
      label,
      assemblyRole: 'ENGINE',
      position: selectedAssembly?.position
    });
    return raw;
  };

  const deriveLiveRecord = (record, treatingAssemblyType) => {
    const unit = getUnitForRecord(record);
    if (!liveValuesAvailable) return record;
    if (!unit) return record;

    let liveElapsedNumeric = null;

    if (unit === 'Hours' && treatingAssemblyType === 'ENGINE') {
      const raw = engineHoursRaw(record?.label);
      if (raw == null) return record;
      liveElapsedNumeric = parseValueByUnit(raw, 'Hours');
    } else if (treatingAssemblyType === 'Airframe') {
      const raw = airframeTotalRaw(unit, record?.label);
      if (raw == null) return record;
      liveElapsedNumeric = parseValueByUnit(raw, unit);
    } else {
      // Non-airframe assembly: shift elapsed using aircraft-level live total for this unit vs parent/self snapshots.
      const raw = airframeTotalRaw(unit, record?.label);
      if (raw == null) return record;
      const parentInstallRaw = record?.parentAssemblyValueAtInstallation?.value;
      const selfInstallRaw = record?.assemblySelfValueAtInstallation?.value;
      if (!toNonEmpty(parentInstallRaw) || !toNonEmpty(selfInstallRaw)) return record;

      const airCurrent = parseValueByUnit(raw, unit);
      const parentInstall = parseValueByUnit(parentInstallRaw, unit);
      const selfInstall = parseValueByUnit(selfInstallRaw, unit);
      liveElapsedNumeric = selfInstall + (airCurrent - parentInstall);
    }

    if (liveElapsedNumeric == null) return record;

    const limitRaw = record?.limit?.value;
    let liveRemainingNumeric = null;

    if (toNonEmpty(limitRaw)) {
      const limitNumeric = parseValueByUnit(limitRaw, unit);
      liveRemainingNumeric = limitNumeric - liveElapsedNumeric;
    } else {
      const elapsedSnapshotNumeric = parseValueByUnit(record?.elapsedTime?.value, unit);
      const remainingSnapshotNumeric = parseValueByUnit(record?.remainingTime?.value, unit);
      const dueAtSnapshotNumeric = elapsedSnapshotNumeric + remainingSnapshotNumeric;
      liveRemainingNumeric = dueAtSnapshotNumeric - liveElapsedNumeric;
    }

    const clampedRemaining = Math.max(0, liveRemainingNumeric);
    const clampedElapsed = Math.max(0, liveElapsedNumeric);

    const elapsedDisplay = formatValueByUnit(clampedElapsed, unit);
    const remainingDisplay = formatValueByUnit(clampedRemaining, unit);

    return {
      ...record,
      elapsedTime: { ...record.elapsedTime, value: elapsedDisplay },
      remainingTime: { ...record.remainingTime, value: remainingDisplay }
    };
  };

  const selectedTreatingAssemblyType = selectedAssembly?.typeName === 'Airframe'
    ? 'Airframe'
    : selectedAssembly?.typeName === 'ENGINE'
      ? 'ENGINE'
      : 'OTHER';

  const derivedRecords = records.map((r) => deriveLiveRecord(r, selectedTreatingAssemblyType));
  const derivedAirframeRecords = airframeRecords.map((r) => deriveLiveRecord(r, 'Airframe'));

  return (
    <Grid item xs={12} sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h6">As compared to Current (Live Data)</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            Live elapsed/remaining are derived from the aircraft&apos;s current usage totals (live/current/total).
            Remaining is recomputed from limits when present (or shifted to keep due-at constant).
          </Typography>
        </Box>
      </Box>

      {airframeError && (
        <Box sx={{ mb: 1.5, bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
          <Typography variant="body2" color="error">
            {airframeError}
          </Typography>
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell sx={{ minWidth: 200 }}>Label</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>LIVE Elapsed (This Assembly)</TableCell>
              <TableCell>Remaining (This Assembly)</TableCell>
              <TableCell>Due At (This Assembly)</TableCell>
              <TableCell>Due At Airframe Assembly</TableCell>
              <TableCell>Due At Parent Assembly</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hasRecords ? (
              derivedRecords.map((record, index) => {
                const unit = getUnitForRecord(record);

                const liveElapsed = getDisplayValue(record?.elapsedTime?.value);
                const remaining = getDisplayValue(record?.remainingTime?.value);

                const selfComparison = computeAssemblySelfComparison(record);
                const dueAtThisAssembly = getDisplayValue(selfComparison.dueAtComponent);

                let dueAtAirframe = null;
                let airframeMessage = null;
                if (airframe && Array.isArray(derivedAirframeRecords) && derivedAirframeRecords.length > 0) {
                  const comparison = computeAssemblyComparison(record, derivedAirframeRecords);
                  dueAtAirframe = getDisplayValue(comparison.dueAtAssembly);
                  airframeMessage = comparison.message;
                }

                let dueAtParent = null;
                if (!isAirframeAssembly) {
                  // Current phase: Parent = Airframe for non-Airframe assemblies
                  dueAtParent = dueAtAirframe;
                }

                const showAirframeNotes = airframeMessage && !dueAtAirframe;
                const showEngineCallout = selectedAssembly?.typeName === 'ENGINE';
                const engineCode = showEngineCallout ? (generateAssemblyCode(selectedAssembly) || '') : '';

                return (
                  <TableRow key={index}>
                    <TableCell>{record?.type}</TableCell>
                    <TableCell>{record?.label}</TableCell>
                    <TableCell>{unit}</TableCell>
                    <TableCell>{liveElapsed || 'N/A'}</TableCell>
                    <TableCell>{remaining || 'N/A'}</TableCell>
                    <TableCell>
                      {dueAtThisAssembly || 'N/A'}
                      {showEngineCallout && engineCode && (
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}
                        >
                          {engineCode} ({String(selectedAssembly?.position || '').trim() || '—'})
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {dueAtAirframe || 'N/A'}
                      {showAirframeNotes && (
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}
                        >
                          {airframeMessage}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{isAirframeAssembly ? 'N/A' : (dueAtParent || 'N/A')}</TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Add Time Since Records to see current live values here.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

export default AssemblyLiveComparisonSection;

